<template>
  <div class="capture-management-page">
    <!-- Header -->
    <div class="page-header">
      <h1>Configuration Capture Management</h1>
      <p class="subtitle">
        Review and approve automated configuration captures
      </p>
    </div>

    <!-- Client Selection -->
    <div class="client-selection">
      <label for="clientSelect">Select Client:</label>
      <select
        id="clientSelect"
        v-model="selectedClientCode"
        @change="handleClientChange"
        class="client-select"
      >
        <option value="">-- Select a client --</option>
        <option
          v-for="client in clients"
          :key="client.code"
          :value="client.code"
        >
          {{ client.code }} - {{ client.name }}
        </option>
      </select>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading client data...</p>
    </div>

    <!-- Main Content (only show when client selected) -->
    <div v-else-if="selectedClientCode" class="main-content">
      <!-- Client Overview Card -->
      <div class="overview-card">
        <h2>Client Overview</h2>
        <div class="overview-stats">
          <div class="stat-item">
            <span class="stat-label">Last Capture:</span>
            <span class="stat-value">{{
              formatDate(clientOverview.lastCapture)
            }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Last Merge:</span>
            <span class="stat-value">{{
              formatDate(clientOverview.lastMerge)
            }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Pending Reviews:</span>
            <span class="stat-value highlight">{{
              clientOverview.pendingReviews
            }}</span>
          </div>
        </div>
      </div>

      <!-- View Tabs -->
      <div class="view-tabs">
        <button
          :class="['tab', { active: currentView === 'captures' }]"
          @click="switchView('captures')"
        >
          📦 Capture Review
        </button>
        <button
          :class="['tab', { active: currentView === 'baseline' }]"
          @click="switchView('baseline')"
        >
          📊 Baseline Compliance
        </button>
      </div>

      <!-- Captures View -->
      <div v-if="currentView === 'captures'" class="view-content">
        <!-- Capture Selector Component -->
        <CaptureSelector
          :client-code="selectedClientCode"
          :branches="captureBranches"
          :selected-branch="selectedBranch"
          @branch-selected="handleBranchSelected"
          @refresh="loadCaptureBranches"
        />

        <!-- Review Workflow (only show when branch selected) -->
        <CaptureReviewWorkflow
          v-if="selectedBranch"
          :client-code="selectedClientCode"
          :branch-name="selectedBranch"
          :repo-id="repoId"
          @review-complete="handleReviewComplete"
          @merge-complete="handleMergeComplete"
        />
      </div>

      <!-- Baseline Compliance View -->
      <div v-else-if="currentView === 'baseline'" class="view-content">
        <BaselineComparisonView
          :client-code="selectedClientCode"
          :repo-id="repoId"
        />
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">📋</div>
      <h3>Select a Client to Begin</h3>
      <p>
        Choose a client from the dropdown above to view their configuration
        captures and baseline compliance
      </p>
    </div>

    <!-- Toast Notifications -->
    <div v-if="toast.show" :class="['toast-notification', toast.type]">
      {{ toast.message }}
    </div>
  </div>
</template>

<script>
import { msalInstance } from "@/auth.js";
import CaptureSelector from "@/views/components/capture/CaptureSelector.vue";
import CaptureReviewWorkflow from "@/views/components/capture/CaptureReviewWorkflow.vue";
import BaselineComparisonView from "@/views/components/capture/BaselineComparisonView.vue";
import {
  getClientBranches,
  getClientOverview,
  fetchClients,
} from "@/azure-devops-api.js";

export default {
  name: "ClientCaptureManagement",

  components: {
    CaptureSelector,
    CaptureReviewWorkflow,
    BaselineComparisonView,
  },

  data() {
    return {
      // Client data
      clients: [],
      selectedClientCode: "",

      // Branch data
      captureBranches: [],
      selectedBranch: null,

      // Overview data
      clientOverview: {
        lastCapture: null,
        lastMerge: null,
        pendingReviews: 0,
      },

      // Configuration
      repoId: "62b297c6-760c-4250-a926-52b8850e6d5f", // Your baseline repo ID

      // UI state
      loading: false,
      currentView: "captures", // 'captures' or 'baseline'

      // Toast notifications
      toast: {
        show: false,
        message: "",
        type: "info", // info, success, warning, error
      },
    };
  },

  async created() {
    await this.loadClients();

    // Auto-select client if passed in route params
    if (this.$route.params.clientCode) {
      this.selectedClientCode = this.$route.params.clientCode;

      // Check for view in query params
      if (this.$route.query.view === "baseline") {
        this.currentView = "baseline";
      }

      await this.handleClientChange();

      // Auto-select branch if passed in query params
      if (this.$route.query.branch) {
        this.selectedBranch = this.$route.query.branch;
      }
    }
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return "Never";

      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Show relative time for recent dates
      if (diffMins < 1) return "Just now";
      if (diffMins < 60)
        return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
      if (diffHours < 24)
        return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

      // Show formatted date for older dates
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    async loadClients() {
      try {
        const accessToken = await this.getAccessToken();

        // Fetch real clients from the Clients/ folder structure
        const clientCodes = await fetchClients(
          accessToken,
          this.repoId,
          "/Clients"
        );

        // Transform to include both code and name
        this.clients = clientCodes.map((code) => ({
          code: code,
          name: `Client ${code}`,
        }));

        // Sort alphabetically by code
        this.clients.sort((a, b) => a.code.localeCompare(b.code));

        console.log(
          `Loaded ${this.clients.length} clients from /Clients folder`
        );
      } catch (error) {
        console.error("Error loading clients:", error);
        this.showToast("Failed to load clients", "error");
      }
    },

    async handleClientChange() {
      if (!this.selectedClientCode) return;

      this.loading = true;
      this.selectedBranch = null;

      try {
        const accessToken = await this.getAccessToken();

        // Load capture branches for this client
        await this.loadCaptureBranches();

        // Load client overview stats
        this.clientOverview = await getClientOverview(
          accessToken,
          this.repoId,
          this.selectedClientCode
        );

        // Update URL
        this.updateUrl();
      } catch (error) {
        console.error("Error loading client data:", error);
        this.showToast("Failed to load client data", "error");
      } finally {
        this.loading = false;
      }
    },

    async loadCaptureBranches() {
      try {
        const accessToken = await this.getAccessToken();

        this.captureBranches = await getClientBranches(
          accessToken,
          this.repoId,
          this.selectedClientCode
        );

        // Auto-select most recent unmerged branch if exists
        const unmergedBranch = this.captureBranches.find((b) => !b.merged);
        if (unmergedBranch && !this.selectedBranch) {
          this.selectedBranch = unmergedBranch.name;
        }
      } catch (error) {
        console.error("Error loading branches:", error);
        this.showToast("Failed to load capture branches", "error");
      }
    },

    switchView(view) {
      this.currentView = view;

      // Clear branch selection when switching to baseline view
      if (view === "baseline") {
        this.selectedBranch = null;
      }

      this.updateUrl();
    },

    updateUrl() {
      const query = {};

      if (this.currentView === "baseline") {
        query.view = "baseline";
      }

      if (this.selectedBranch && this.currentView === "captures") {
        query.branch = this.selectedBranch;
      }

      this.$router.push({
        name: "ClientCaptureManagement",
        params: { clientCode: this.selectedClientCode },
        query: query,
      });
    },

    handleBranchSelected(branchName) {
      this.selectedBranch = branchName;
      this.updateUrl();
    },

    async handleReviewComplete() {
      this.showToast("Review completed successfully", "success");
      await this.loadCaptureBranches();
    },

    async handleMergeComplete(mergeResult) {
      console.log("Merge complete:", mergeResult);

      this.showToast(
        `Pull Request #${mergeResult.pullRequestId} created! Awaiting approval in Azure DevOps.`,
        "success"
      );

      // Clear the selected branch to hide the workflow
      this.selectedBranch = null;

      // Refresh branch list and client overview
      await Promise.all([
        this.loadCaptureBranches(),
        this.loadClientOverview(),
      ]);

      // Clear URL query parameter
      this.$router.replace({
        name: "ClientCaptureManagement",
        params: { clientCode: this.selectedClientCode },
      });
    },

    async loadClientOverview() {
      if (!this.selectedClientCode) return;

      try {
        const accessToken = await this.getAccessToken();
        this.clientOverview = await getClientOverview(
          accessToken,
          this.repoId,
          this.selectedClientCode
        );
      } catch (error) {
        console.error("Error loading client overview:", error);
      }
    },

    showToast(message, type = "info") {
      this.toast = { show: true, message, type };
      setTimeout(() => {
        this.toast.show = false;
      }, 4000);
    },

    async getAccessToken() {
      const accounts = msalInstance.getAllAccounts();
      if (!accounts || !accounts.length) {
        throw new Error("User is not logged in");
      }

      const response = await msalInstance.acquireTokenSilent({
        account: accounts[0],
        scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
      });

      return response.accessToken;
    },
  },
};
</script>

<style scoped>
.capture-management-page {
  padding: 24px;
}

.page-header {
  margin-bottom: 32px;
}

.page-header h1 {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
}

.subtitle {
  margin: 0;
  color: #666;
  font-size: 16px;
}

.client-selection {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

.client-selection label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
}

.client-select {
  width: 100%;
  max-width: 400px;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
}

.client-select:hover {
  border-color: #007bff;
}

.client-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.overview-card {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

.overview-card h2 {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.stat-value.highlight {
  color: #dc3545;
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* View Tabs */
.view-tabs {
  display: flex;
  gap: 0;
  background: white;
  padding: 0;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 0;
}

.tab {
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: #f8f9fa;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s;
  color: #666;
  position: relative;
}

.tab:hover {
  background: #e9ecef;
  color: #495057;
}

.tab.active {
  background: white;
  color: #007bff;
  border-bottom-color: #007bff;
}

.tab:first-child {
  border-right: 1px solid #e0e0e0;
}

/* View Content */
.view-content {
  background: white;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #1a1a1a;
}

.empty-state p {
  margin: 0;
  color: #666;
  font-size: 14px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 16px 24px;
  border-radius: 8px;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
  animation: slideIn 0.3s ease-out;
  max-width: 400px;
}

@keyframes slideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-notification.success {
  border-left: 4px solid #28a745;
  color: #155724;
}

.toast-notification.error {
  border-left: 4px solid #dc3545;
  color: #721c24;
}

.toast-notification.warning {
  border-left: 4px solid #ffc107;
  color: #856404;
}

.toast-notification.info {
  border-left: 4px solid #007bff;
  color: #004085;
}

/* Responsive Design */
@media (max-width: 768px) {
  .capture-management-page {
    padding: 16px;
  }

  .page-header h1 {
    font-size: 24px;
  }

  .overview-stats {
    grid-template-columns: 1fr;
  }

  .view-tabs {
    flex-direction: column;
  }

  .tab:first-child {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }

  .toast-notification {
    left: 16px;
    right: 16px;
    bottom: 16px;
  }
}
</style>
